import { useState } from 'react'
import { Download, Pencil, Plus, Receipt, ShieldAlert, Upload } from 'lucide-react'
import {
  ConfirmDialog, CurrencyValue, EmptyState, ErrorState, FormDialog, ImportDialog, PageHeader,
  PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePayElements } from '@/features/payroll'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { useExpenseLimits, useExpenseMutations, useExpenseTypes } from '../api'
import type { ExpenseTypeView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

/**
 * What may be claimed, and what each type costs against a policy limit.
 *
 * A limit here warns; it never refuses. Somebody who genuinely spent more than the policy
 * allows still has to be able to record it, and the approver has to be able to see by how
 * much — a claim the system silently refused is a claim that gets settled in cash instead.
 */
export function ExpenseTypesPage() {
  useBreadcrumb()
  const types = useExpenseTypes()
  const limits = useExpenseLimits()
  const elements = usePayElements()
  const { saveType, saveLimit, retireType } = useExpenseMutations()

  const [typeDraft, setTypeDraft] = useState<{
    id?: string
    code: string; name: string; description: string; requiresReceipt: boolean
    glAccountCode: string; reimbursementElementId: string
  } | null>(null)
  const [retiring, setRetiring] = useState<ExpenseTypeView | null>(null)
  const [importing, setImporting] = useState(false)
  const [limitDraft, setLimitDraft] = useState<{
    expenseTypeId: string; effectiveFrom: string; maximumPerClaim: string
    maximumPerMonth: string; currency: string; notes: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Expense types"
        subtitle="What may be claimed, where it posts, and how it is paid back."
        actions={
          <PermissionGate permission="expense:configure">
            <Button
              type="button" variant="outline"
              onClick={() => {
                void downloadFile(
                  bulkPaths('/v1/payroll/masters/expense-types').export, 'expense-types.xlsx')
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Export
            </Button>
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import
            </Button>
            <Button
              type="button"
              onClick={() => {
                setError(null)
                setTypeDraft({
                  code: '', name: '', description: '', requiresReceipt: true,
                  glAccountCode: '', reimbursementElementId: '',
                })
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              New type
            </Button>
          </PermissionGate>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="expense types"
        prefix="/v1/payroll/masters/expense-types"
        invalidateKey={['expenses']}
      />

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this type'}?`}
        description="It stops being offered on new claims. Every claim already settled under it keeps naming it — this is never a delete."
        confirmLabel="Retire it"
        loading={retireType.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retireType.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That type could not be retired.'),
          })
        }}
      />

      <Card>
        {types.isPending && <Skeleton className="m-4 h-24" />}
        {types.isError && <ErrorState error={types.error} onRetry={() => void types.refetch()} />}
        {types.isSuccess && types.data.length === 0 && (
          <EmptyState
            title="No expense type is defined"
            description="Nothing can be claimed until one exists. Each says whether a receipt is needed and which account it posts to."
          />
        )}
        {types.isSuccess && types.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Receipt</th>
                  <th className="p-3 text-start font-medium">Posts to</th>
                  <th className="p-3 text-start font-medium">Paid back on</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {types.data.map((type) => (
                  <tr
                    key={type.id}
                    className={`border-b border-slate-100 ${type.active ? '' : 'text-slate-400'}`}
                  >
                    <td className="p-3 font-mono">{type.code}</td>
                    <td className="p-3">{type.name}</td>
                    <td className="p-3 text-slate-600">
                      {type.requiresReceipt ? 'Required' : 'Not required'}
                    </td>
                    <td className="p-3 font-mono text-slate-600">
                      {type.glAccountCode ?? <span className="font-sans">Not mapped</span>}
                    </td>
                    <td className="p-3 text-slate-600">
                      {elements.data?.find((element) => element.id === type.reimbursementElementId)
                        ?.name ?? 'Paid directly'}
                    </td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="expense:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${type.name}`}
                          onClick={() => {
                            setError(null)
                            setTypeDraft({
                              id: type.id,
                              code: type.code,
                              name: type.name,
                              description: type.description ?? '',
                              requiresReceipt: type.requiresReceipt,
                              glAccountCode: type.glAccountCode ?? '',
                              reimbursementElementId: type.reimbursementElementId ?? '',
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {type.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => { setError(null); setRetiring(type) }}
                          >
                            Retire
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PageHeader
        title="Policy limits"
        subtitle="What is normally reasonable, per claim and per month."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null)
              setLimitDraft({
                expenseTypeId: types.data?.[0]?.id ?? '', effectiveFrom: '',
                maximumPerClaim: '', maximumPerMonth: '', currency: 'AED', notes: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New limit
          </Button>
        }
      />

      <Card>
        {limits.isPending && <Skeleton className="m-4 h-20" />}
        {limits.isError && <ErrorState error={limits.error} onRetry={() => void limits.refetch()} />}
        {limits.isSuccess && limits.data.length === 0 && (
          <EmptyState
            title="No limit is set"
            description="Without one, every claim is simply what was spent — nothing is flagged as over policy."
          />
        )}
        {limits.isSuccess && limits.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Type</th>
                  <th className="p-3 text-start font-medium">Grade</th>
                  <th className="p-3 text-start font-medium">From</th>
                  <th className="p-3 text-end font-medium">Per claim</th>
                  <th className="p-3 text-end font-medium">Per month</th>
                </tr>
              </thead>
              <tbody>
                {limits.data.map((limit) => (
                  <tr key={limit.id} className="border-b border-slate-100">
                    <td className="p-3">{limit.expenseTypeName}</td>
                    <td className="p-3 text-slate-600">{limit.gradeName ?? 'Every grade'}</td>
                    <td className="p-3 text-slate-600">{limit.effectiveFrom}</td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={limit.maximumPerClaim} currency={limit.currency} />
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={limit.maximumPerMonth} currency={limit.currency} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A limit warns, it does not refuse. A line over the limit is saved with the limit and the
        excess recorded on it, so the approver decides — and can approve for less than was claimed.
      </p>

      <FormDialog
        open={typeDraft != null}
        onOpenChange={(open) => { if (!open) setTypeDraft(null) }}
        title={typeDraft?.id ? 'Edit expense type' : 'New expense type'}
        description="Codes are how the type is referred to in imports and reports, so they cannot change later."
        submitLabel="Save type"
        loading={saveType.isPending}
        error={error}
        onSubmit={() => {
          if (!typeDraft) return
          void (async () => {
            setError(null)
            try {
              await saveType.mutateAsync({
                id: typeDraft.id,
                code: typeDraft.code,
                name: typeDraft.name,
                description: typeDraft.description || null,
                requiresReceipt: typeDraft.requiresReceipt,
                glAccountCode: typeDraft.glAccountCode || null,
                reimbursementElementId: typeDraft.reimbursementElementId || null,
              })
              setTypeDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That type could not be saved')
            }
          })()
        }}
      >
        {typeDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="type-code">Code</Label>
                <Input
                  id="type-code" value={typeDraft.code}
                  onChange={(event) =>
                    setTypeDraft({ ...typeDraft, code: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="type-name">Name</Label>
                <Input
                  id="type-name" value={typeDraft.name}
                  onChange={(event) => setTypeDraft({ ...typeDraft, name: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="type-gl">Account code</Label>
                <Input
                  id="type-gl" value={typeDraft.glAccountCode}
                  onChange={(event) =>
                    setTypeDraft({ ...typeDraft, glAccountCode: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Left blank, the claim posts as unmapped and the journal says so.
                </p>
              </div>
              <div>
                <Label htmlFor="type-element">Paid back through</Label>
                <Select
                  id="type-element"
                  className="w-full"
                  value={typeDraft.reimbursementElementId}
                  onChange={(event) =>
                    setTypeDraft({ ...typeDraft, reimbursementElementId: event.target.value })}
                >
                  <option value="">Paid directly, outside payroll</option>
                  {elements.data?.map((element) => (
                    <option key={element.id} value={element.id}>{element.name}</option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="type-description">Description</Label>
              <Input
                id="type-description" value={typeDraft.description}
                onChange={(event) =>
                  setTypeDraft({ ...typeDraft, description: event.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={typeDraft.requiresReceipt}
                onChange={(event) =>
                  setTypeDraft({ ...typeDraft, requiresReceipt: event.target.checked })}
              />
              A receipt has to be attached to every line
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={limitDraft != null}
        onOpenChange={(open) => { if (!open) setLimitDraft(null) }}
        title="New policy limit"
        description="At least one of the two maximums has to be set — a limit that caps nothing is refused."
        submitLabel="Save limit"
        loading={saveLimit.isPending}
        error={error}
        onSubmit={() => {
          if (!limitDraft) return
          void (async () => {
            setError(null)
            try {
              await saveLimit.mutateAsync({
                expenseTypeId: limitDraft.expenseTypeId,
                effectiveFrom: limitDraft.effectiveFrom,
                maximumPerClaim: limitDraft.maximumPerClaim
                  ? Number(limitDraft.maximumPerClaim) : null,
                maximumPerMonth: limitDraft.maximumPerMonth
                  ? Number(limitDraft.maximumPerMonth) : null,
                currency: limitDraft.currency,
                notes: limitDraft.notes || null,
              })
              setLimitDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That limit could not be saved')
            }
          })()
        }}
      >
        {limitDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="limit-type">Expense type</Label>
              <Select
                id="limit-type"
                className="w-full"
                value={limitDraft.expenseTypeId}
                onChange={(event) =>
                  setLimitDraft({ ...limitDraft, expenseTypeId: event.target.value })}
              >
                <option value="">Choose a type…</option>
                {types.data?.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="limit-from">In force from</Label>
              <DatePicker
                id="limit-from" value={limitDraft.effectiveFrom}
                onChange={(event) =>
                  setLimitDraft({ ...limitDraft, effectiveFrom: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="limit-claim">Most per claim</Label>
              <Input
                id="limit-claim" type="number" value={limitDraft.maximumPerClaim}
                onChange={(event) =>
                  setLimitDraft({ ...limitDraft, maximumPerClaim: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="limit-month">Most per month</Label>
              <Input
                id="limit-month" type="number" value={limitDraft.maximumPerMonth}
                onChange={(event) =>
                  setLimitDraft({ ...limitDraft, maximumPerMonth: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="limit-currency">Currency</Label>
              <Input
                id="limit-currency" maxLength={3} value={limitDraft.currency}
                onChange={(event) =>
                  setLimitDraft({ ...limitDraft, currency: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="limit-notes">Notes</Label>
              <Input
                id="limit-notes" value={limitDraft.notes}
                onChange={(event) => setLimitDraft({ ...limitDraft, notes: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Receipt className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A type set to require a receipt refuses a line without one, because a claim with no
        evidence is one that cannot be defended in an audit.
      </p>
    </div>
  )
}
